import { CalendarIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { formatDate } from '@/lib/date/date'
import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Input } from '@/components/lemmala-design-system/input/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/lemmala-design-system/popover/popover'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { DatePickerCalendar } from './date-picker-calendar'
import { DatePickerProps } from './types'

export type { DatePickerProps } from './types'

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onClear,
  disabledDates,
  className,
  inputClassName,
  disabled = false,
  placeholder = 'Pick a date',
  state = 'default',
  inputSize = 'default',
  container,
  defaultOpen = false,
  variant = 'input',
  isEditing: externalIsEditing,
  onEditingChange,
  onOpen,
  emptyStateIcon = <CalendarIcon className='size-4' />,
  emptyStateText,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [internalIsEditing, setInternalIsEditing] = useState(false)

  // Use external editing state if provided, otherwise use internal state
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing
  const setIsEditing = onEditingChange || setInternalIsEditing

  // Auto-open popover when transitioning to edit mode in display variant
  useEffect(() => {
    if (variant !== 'input' && isEditing && !isOpen) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsOpen(true)
    }
  }, [variant, isEditing, isOpen])

  // Handle defaultOpen for display variant
  useEffect(() => {
    if (variant !== 'input' && defaultOpen && isEditing) {
      // oxlint-disable-next-line react/set-state-in-effect
      setIsOpen(true)
    }
  }, [variant, defaultOpen, isEditing])

  const handleDateSelect = (date: Date) => {
    onChange?.(date)
    setIsOpen(false)
    if (variant !== 'input') {
      setIsEditing(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClear?.()
    if (variant !== 'input') {
      setIsEditing(false)
    }
  }

  const handleClick = () => {
    if (!disabled && variant !== 'input' && !isEditing) {
      setIsEditing(true)
      onOpen?.()
      // The useEffect will automatically open the popover
    }
  }

  const displayValue = value ? formatDate(value) : ''
  const finalEmptyStateText = emptyStateText || `Click to select ${placeholder.toLowerCase()}`

  // Button variant in non-editing mode
  if (variant === 'button' && !isEditing) {
    return (
      <Button
        onClick={handleClick}
        variant='ghost-secondary'
        size='sm'
        className={disabled ? 'cursor-not-allowed' : ''}
      >
        {value ? (
          <TextWithIcon icon={<CalendarIcon className='size-4' />} text={formatDate(value)} />
        ) : (
          <TextWithIcon icon={emptyStateIcon} text={finalEmptyStateText} />
        )}
      </Button>
    )
  }

  // Display variant in non-editing mode
  if (variant === 'display' && !isEditing) {
    return (
      <div
        className={cn(
          'w-48 cursor-pointer whitespace-pre-wrap py-2.5',
          disabled && 'cursor-not-allowed',
        )}
        onClick={handleClick}
      >
        {value ? (
          <TextWithIcon
            className='text-main-foreground-primary text-body-default'
            icon={<CalendarIcon className='size-4' />}
            text={formatDate(value)}
          />
        ) : (
          <TextWithIcon
            className='text-main-foreground-secondary text-body-default'
            icon={emptyStateIcon}
            text={finalEmptyStateText}
          />
        )}
      </div>
    )
  }

  // Handle popover close - if in display variant, also exit edit mode
  const handlePopoverOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open && variant !== 'input') {
      setIsEditing(false)
    }
  }

  // Input variant or display variant in editing mode
  return (
    <div className={cn('relative', className)}>
      <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <div className='relative w-48'>
            <Input
              readOnly
              disabled={disabled}
              value={displayValue}
              placeholder={placeholder}
              state={state}
              inputSize={inputSize}
              iconLeft={<CalendarIcon className='size-4' />}
              className={cn(
                'cursor-pointer pr-8',
                disabled && 'cursor-not-allowed',
                isOpen && 'ring-offset !border-main-border-focus ring-main-border-focus',
                inputClassName,
              )}
              onClick={() => !disabled && setIsOpen(true)}
            />
            {value && onClear && !disabled && (
              <button
                type='button'
                onClick={handleClear}
                className='absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 transition-colors hover:bg-main-background-interactive-list-hover'
                aria-label='Clear date'
              >
                <XIcon className='size-4 text-main-foreground-secondary' />
              </button>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          container={container}
          className='z-max w-auto border-none p-0'
          align='start'
          sideOffset={4}
        >
          <DatePickerCalendar
            value={value}
            onChange={handleDateSelect}
            disabledDates={disabledDates}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

DatePicker.displayName = 'DatePicker'
